import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { adminService } from '../../../services/adminService';
import { fmt } from '../../../utils/formatters';

interface TermsDoc {
  id: number;
  version: string;
  title: string;
  body: string;
  is_active: boolean;
  require_reaccept: boolean;
  created_at: string;
  updated_at: string;
}

const TermsAdmin: React.FC = () => {
  const [docs, setDocs] = useState<TermsDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Editor state
  const [editOpen, setEditOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<Partial<TermsDoc> | null>(null);
  const [originalVersion, setOriginalVersion] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminService.listTerms();
      setDocs(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditDoc({ version: '', title: 'Terms and Conditions', body: '', is_active: true, require_reaccept: true });
    setEditOpen(true);
    setError(null);
    setSuccess(null);
  };

  const openEdit = (doc: TermsDoc) => {
    setEditDoc({ ...doc });
    setOriginalVersion(doc.version);
    setEditOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!editDoc) return;
    setSaving(true);
    setError(null);
    try {
      const versionChanged = editDoc.id && editDoc.version !== originalVersion;
      if (editDoc.id && !versionChanged) {
        // Same version — update in place
        await adminService.updateTerms(editDoc.id, {
          version: editDoc.version,
          title: editDoc.title,
          body: editDoc.body,
          is_active: editDoc.is_active,
          require_reaccept: editDoc.require_reaccept,
        });
        setSuccess(`Terms v${editDoc.version} updated.`);
      } else {
        await adminService.createTerms({
          version: editDoc.version || '',
          title: editDoc.title || '',
          body: editDoc.body || '',
          is_active: editDoc.is_active || false,
          require_reaccept: editDoc.require_reaccept || false,
        });
        setSuccess(`Terms v${editDoc.version} created${versionChanged ? ' (saved as new version)' : ''}.`);
      }
      setEditOpen(false);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Terms & Conditions
        </Typography>
        <Button variant="contained" onClick={openNew}>
          New Version
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Re-accept</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{doc.version}</TableCell>
                  <TableCell>{doc.title}</TableCell>
                  <TableCell>
                    {doc.is_active ? (
                      <Chip label="Active" color="success" size="small" />
                    ) : (
                      <Chip label="Inactive" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{doc.require_reaccept ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{fmt.date(doc.created_at)}</TableCell>
                  <TableCell>{fmt.date(doc.updated_at)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => openEdit(doc)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {docs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                    No terms documents yet. Click "New Version" to create one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Editor Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editDoc?.id ? `Edit Terms v${originalVersion}` : 'Create New Terms'}
          {editDoc?.id && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 400 }}>
              Change the version number to save as a new version
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Version"
              placeholder="e.g. 1.0"
              size="small"
              value={editDoc?.version || ''}
              onChange={(e) => setEditDoc((d) => d ? { ...d, version: e.target.value } : d)}
              sx={{ width: 120 }}
            />
            <TextField
              label="Title"
              size="small"
              fullWidth
              value={editDoc?.title || ''}
              onChange={(e) => setEditDoc((d) => d ? { ...d, title: e.target.value } : d)}
            />
          </Box>
          <TextField
            label="Body (plain text)"
            multiline
            minRows={12}
            maxRows={20}
            fullWidth
            value={editDoc?.body || ''}
            onChange={(e) => setEditDoc((d) => d ? { ...d, body: e.target.value } : d)}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editDoc?.is_active || false}
                  onChange={(e) => setEditDoc((d) => d ? { ...d, is_active: e.target.checked } : d)}
                />
              }
              label="Set as active (deactivates previous version)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editDoc?.require_reaccept || false}
                  onChange={(e) => setEditDoc((d) => d ? { ...d, require_reaccept: e.target.checked } : d)}
                />
              }
              label="Require all users to re-accept"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TermsAdmin;
